interface StudioShadowSnapshot {
  readonly revision: number;
  readonly yaw: number;
}

interface StudioShadowRefreshOptions {
  invalidate: () => void;
  /** Synchronous capture; a throw leaves the last successful state intact. */
  refresh: (snapshot: StudioShadowSnapshot, revisionChanged: boolean) => void;
  /** Minimum milliseconds between captures. Invalid/negative values mean 0. */
  updateIntervalMs?: number;
  /**
   * The same while the user controls the camera (`update`'s `controlled`).
   * Defaults to 0: the shadow follows a user orbit on every frame.
   */
  controlledUpdateIntervalMs?: number;
  /** Monotonic milliseconds, independent of the demand loop's clock. */
  now?: () => number;
}

const validInterval = (value: number) =>
  Number.isFinite(value) ? Math.max(0, value) : 0;

function sameYaw(left: number, right: number): boolean {
  const difference = left - right;
  return (
    Math.abs(Math.atan2(Math.sin(difference), Math.cos(difference))) < 1e-4
  );
}

/**
 * Throttles shadow captures, not scene activity. A dirty frame inside the
 * budget arms one wake at the next capture deadline, without moving that
 * deadline on further changes. The wake only invalidates: the next frame
 * supplies the latest revision/yaw, even when controls have stopped.
 *
 * No timers are armed by a successful capture or by an unchanged frame.
 */
export function createStudioShadowRefresh({
  invalidate,
  refresh,
  updateIntervalMs = 0,
  controlledUpdateIntervalMs = 0,
  now = () => performance.now(),
}: StudioShadowRefreshOptions) {
  const interval = validInterval(updateIntervalMs);
  const controlledInterval = validInterval(controlledUpdateIntervalMs);
  let captured: StudioShadowSnapshot | undefined;
  let capturedAt = Number.NEGATIVE_INFINITY;
  let timeout: ReturnType<typeof setTimeout> | undefined;
  let wakeAt = Number.POSITIVE_INFINITY;
  let disposed = false;

  const cancelWake = () => {
    if (timeout !== undefined) clearTimeout(timeout);
    timeout = undefined;
    wakeAt = Number.POSITIVE_INFINITY;
  };

  return {
    update(revision: number, yaw: number, controlled = false): boolean {
      if (disposed) return false;
      const revisionChanged = captured?.revision !== revision;
      if (captured && !revisionChanged && sameYaw(yaw, captured.yaw)) {
        // Motion may have returned to the already captured pose.
        cancelWake();
        return false;
      }

      const timestamp = now();
      const remaining =
        (controlled ? controlledInterval : interval) - (timestamp - capturedAt);
      if (remaining > 0) {
        // Never moves a wake later; a shorter interval (control starting with
        // an animation wake pending) brings it forward.
        if (timestamp + remaining < wakeAt) {
          cancelWake();
          wakeAt = timestamp + remaining;
          timeout = setTimeout(() => {
            timeout = undefined;
            wakeAt = Number.POSITIVE_INFINITY;
            if (!disposed) invalidate();
          }, Math.ceil(remaining));
        }
        return false;
      }

      // A normal frame can satisfy the deadline before the timeout fires.
      cancelWake();
      const snapshot = { revision, yaw };
      refresh(snapshot, revisionChanged);
      // Commit only after the entire capture (including restoration) succeeds.
      captured = snapshot;
      capturedAt = timestamp;
      return true;
    },
    dispose(): void {
      disposed = true;
      cancelWake();
    },
  };
}
